/**
 * This is a Pill Component.This component can used in the form to show the active, disabled, default state and disabled active.This component can be used to display the state of the Application too
 *
 * @module views/components/Pill
 * @memberof -Common
 */
import './Pill.scss';

import React, { useCallback } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { spreadProps } from '@ulta/core/utils/dom/dom';


/**
 * A click handler for disabled button
 * @method
 * @param {object} event
 * @returns false
 */
export const disabledHandler = ( event ) => {
  event.preventDefault();
  return false;
};

/**
 * Represents a Pill component
 *
 * @method
 * @param {PillProps} props - React properties passed from composition
 * @returns Pill
 */
export const Pill = function( props ){
  const {
    active = defaultProps.active,
    disabled = defaultProps.disabled,
    unavailable = defaultProps.unavailable,
    onClick,
    selectAction,
    linkSelectAction = {},
    message,
    showSelectedCheckmark = defaultProps.showSelectedCheckmark
  } = props;
  const ariaCurrent = active ? 'true' : 'false';
  const iconImage = getIconImage( { active, showSelectedCheckmark } );
  const handleOnClick = useCallback( () => {
    ( !selectAction.url );
    onClick( selectAction );
  }, [selectAction, onClick] );

  const pillLink = (
    <Link_Huge
      className={ classNames( 'Pill', {
        'Pill--active': active,
        'Pill--disabled': disabled,
        'Pill--disabledActive': active && disabled,
        'Pill--unavailable': unavailable,
        'Pill--unavailableActive': active && unavailable
      } ) }
      ariaCurrent={ ariaCurrent }
      disabled={ disabled }
      onClick={ ( event ) => {
        event.preventDefault();
        handleOnClick();
      } }
      url={ linkSelectAction?.url }
      target={ linkSelectAction?.target }
    >
      <Text
        htmlTag='span'
        textStyle='body-2'
      >
        { message }
      </Text>
    </Link_Huge>
  );
  const pillButton = (
    <Button
      type='button'
      className={ classNames( 'Pill', {
        'Pill--active': active,
        'Pill--disabled': disabled,
        'Pill--disabledActive': active && disabled,
        'Pill--unavailable': unavailable,
        'Pill--unavailableActive': active && unavailable
      } ) }
      onClick={ disabled ? disabledHandler : handleOnClick }
      disabled={ disabled }
      variant='unstyled'
      action={ selectAction }
      { ...spreadProps( {
        ...iconImage
      } ) }
      iconSize='s'
      ariaHiddenIcon={ true }
      ariaPressed={ active }
    >
      <Text
        htmlTag='span'
        textStyle='body-2'
      >
        { message }
      </Text>
    </Button>
  );

  const pillElement = linkSelectAction?.url ? pillLink : pillButton;

  return (
    <React.Fragment>
      { pillElement }
    </React.Fragment>
  );
};

/**
 * Represents a getIconImage method
 * @param {object} data - Arguments
 * @param {object} data.active - true or false
 * @param {object} data.showSelectedCheckmark - true or false
 * @returns image Name or undefined
 */

export const getIconImage = ( data ) => {
  const { active, showSelectedCheckmark } = data || {};
  if( active && showSelectedCheckmark ){
    return checkImageName;
  }
};

// const for check image icon
export const checkImageName = 'Check';
/**
 * Property type definitions
 * @typedef PillProps
 * @type {object}
 * @property {boolean} active - Sets when pill is active
 * @property {boolean} disabled - Sets when pill is disabled
 * @property {boolean} unavailable - Sets when pill is unavailable
 * @property {string} message - Sets the message for the Pill
 * @property {Function} onClick - Function to be called on button click
 * @property {boolean} showSelectedCheckmark - Set to true to show check Image
 */
export const propTypes = {
  /** Sets when pill is active */
  active: PropTypes.bool,
  /** Sets when pill is disabled */
  disabled: PropTypes.bool,
  /** Sets when pill is unavailable */
  unavailable: PropTypes.bool,
  /** Sets message for the Pill */
  message: PropTypes.string.isRequired,
  /** Function to be called on button click */
  onClick: PropTypes.func,
  /** set to true to show check Image */
  showSelectedCheckmark: PropTypes.bool
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {boolean} active=false - Set the default pill as inactive
 * @property {boolean} disabled=false - Set the default pill as enabled
 * @property {boolean} unavailable=false - Set the default pill as available
 * @property {boolean} showSelectedCheckmark=false -set the default pill is unchecked
 */
export const defaultProps = {
  active: false,
  disabled: false,
  unavailable: false,
  showSelectedCheckmark: false
};

Pill.propTypes = propTypes;
Pill.defaultProps = defaultProps;

export default Pill;